import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Trash2, Image as ImageIcon, Video as VideoIcon, X, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

const galleryUrl = (filename) => {
  const t = typeof window !== "undefined" ? localStorage.getItem("gl26_auth_token") : null;
  const base = `${API}/files/gallery/${filename}`;
  return t ? `${base}?t=${encodeURIComponent(t)}` : base;
};

export const GalleryDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState(null); // item

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gallery`);
      setItems(data || []);
    } catch (e) {
      toast.error("Impossible de charger la galerie");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const del = async (item) => {
    if (!window.confirm(`Supprimer cet${item.media_type === "video" ? "te vidéo" : "te photo"} ?`)) return;
    try {
      await axios.delete(`${API}/gallery/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (preview?.id === item.id) setPreview(null);
      toast.success("Supprimé");
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const photos = items.filter((i) => i.media_type === "photo");
  const videos = items.filter((i) => i.media_type === "video");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="bg-[#0d0d0d] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto no-scrollbar"
          data-testid="gallery-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg text-[var(--gl-gold)] font-display flex items-center gap-2">
              <ImageIcon size={18} /> Galerie du concert
            </DialogTitle>
            <DialogDescription className="text-neutral-400 text-xs">
              Photos et vidéos capturées pendant le concert. Stockage illimité, partagé.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-3 text-xs text-neutral-400 px-1">
            <span className="flex items-center gap-1.5" data-testid="gallery-count-photos">
              <ImageIcon size={14} className="text-[var(--gl-gold)]" /> {photos.length} photo{photos.length > 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1.5" data-testid="gallery-count-videos">
              <VideoIcon size={14} className="text-[var(--gl-neon)]" /> {videos.length} vidéo{videos.length > 1 ? "s" : ""}
            </span>
          </div>

          {loading ? (
            <div className="py-12 text-center text-neutral-500 text-sm">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-neutral-500 text-sm py-10 border border-dashed border-white/10 rounded-xl">
              Aucune capture pour l&apos;instant.
              <br />
              Utilise les boutons <span className="text-[var(--gl-gold)] font-bold">📷 Photo</span> ou{" "}
              <span className="text-[var(--gl-neon)] font-bold">🎥 Vidéo</span> pendant le concert.
            </div>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5" data-testid="gallery-grid">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="relative group rounded-xl overflow-hidden bg-black border border-white/10 hover:border-[var(--gl-gold)]/50 transition aspect-square"
                  data-testid={`gallery-item-${item.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setPreview(item)}
                    className="absolute inset-0 w-full h-full"
                    aria-label={item.media_type === "video" ? "Voir la vidéo" : "Voir la photo"}
                  >
                    {item.media_type === "photo" ? (
                      <img
                        src={galleryUrl(item.filename)}
                        alt={item.caption || item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full relative bg-[#111]">
                        <video
                          src={galleryUrl(item.filename)}
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                          playsInline
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-10 h-10 rounded-full bg-[var(--gl-neon)] text-black flex items-center justify-center">
                            <Play size={18} className="ml-0.5" />
                          </div>
                        </div>
                      </div>
                    )}
                  </button>
                  <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-black/70 text-white flex items-center gap-1">
                    {item.media_type === "video" ? (
                      <><VideoIcon size={10} className="text-[var(--gl-neon)]" /> Vidéo</>
                    ) : (
                      <><ImageIcon size={10} className="text-[var(--gl-gold)]" /> Photo</>
                    )}
                  </div>
                  <button
                    onClick={() => del(item)}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/80 border border-white/15 hover:border-orange-400 hover:text-orange-400 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    data-testid={`gallery-delete-${item.id}`}
                    aria-label="Supprimer"
                  >
                    <Trash2 size={13} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/90 to-transparent text-[10px] text-neutral-300 tabular-nums">
                    {fmtDate(item.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview overlay */}
      {preview && (
        <div
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="gallery-preview"
        >
          <button
            className="absolute top-3 right-3 w-11 h-11 rounded-full bg-black/80 border border-white/20 text-white flex items-center justify-center hover:border-[var(--gl-gold)]"
            onClick={(e) => { e.stopPropagation(); setPreview(null); }}
            data-testid="gallery-preview-close"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
          <div
            className="max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {preview.media_type === "photo" ? (
              <img
                src={galleryUrl(preview.filename)}
                alt={preview.caption || preview.filename}
                className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg"
              />
            ) : (
              <video
                src={galleryUrl(preview.filename)}
                className="max-w-[95vw] max-h-[90vh] rounded-lg"
                controls
                autoPlay
                playsInline
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryDialog;
